import { useState, useEffect, useRef } from 'react'
import { ChevronDown, Printer, FileText, RefreshCw } from 'lucide-react'
import leiloesService from '../../services/leiloesService'
import mapasService from '../../services/mapasService'
import { downloadPdf } from '../../utils/pdfUtils'
import { useOrganizador } from '../../contexts/OrganizadorContext'

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')
const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const fmtDate = s => {
  if (!s) return ''
  try {
    const d = s.split('T')[0]
    const [y, m, day] = d.split('-')
    return `${day}/${m}/${y}`
  } catch { return s }
}

function Select({ label, value, onChange, children, disabled, className = '' }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && <label className="text-xs text-gray-500 font-medium">{label}</label>}
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400">
          {children}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  )
}

const S = {
  page: { fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 13, color: '#000', background: '#fff', padding: '20px 28px 24px' },
  hdr: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #1f2937', paddingBottom: 14, marginBottom: 18 },
  hdrLogo: { maxHeight: 80, maxWidth: 180, objectFit: 'contain' },
  hdrCenter: { flex: 1, textAlign: 'center', padding: '0 16px' },
  hdrT1: { fontSize: 20, fontWeight: 700 },
  hdrT2: { fontSize: 12, color: '#374151', marginTop: 3 },
  hdrT3: { fontSize: 13, fontWeight: 600, color: '#1f2937', marginTop: 2 },
  compBanner: { background: '#f3f4f6', border: '1px solid #d1d5db', color: '#1f2937', padding: '10px 16px', textAlign: 'center', fontWeight: 700, fontSize: 15, marginBottom: 0 },
  compGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px 16px', fontSize: 12, padding: '10px 16px', border: '1px solid #d1d5db', borderTop: 'none', marginBottom: 16 },
  compLbl: { fontWeight: 700 },
  secTitle: { fontSize: 13, fontWeight: 700, color: '#1f2937', marginTop: 14, marginBottom: 6 },
  tbl: { width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 8 },
  th: { background: '#f3f4f6', border: '1px solid #d1d5db', padding: '6px 8px', textAlign: 'left', fontWeight: 700, fontSize: 11 },
  thC: { background: '#f3f4f6', border: '1px solid #d1d5db', padding: '6px 8px', textAlign: 'center', fontWeight: 700, fontSize: 11 },
  thR: { background: '#f3f4f6', border: '1px solid #d1d5db', padding: '6px 8px', textAlign: 'right', fontWeight: 700, fontSize: 11 },
  td: { border: '1px solid #e5e7eb', padding: '6px 8px', verticalAlign: 'middle', fontSize: 12 },
  tdC: { border: '1px solid #e5e7eb', padding: '6px 8px', textAlign: 'center', verticalAlign: 'middle', fontSize: 12 },
  tdR: { border: '1px solid #e5e7eb', padding: '6px 8px', textAlign: 'right', verticalAlign: 'middle', fontSize: 12 },
  footer: { textAlign: 'left', fontSize: 11, color: '#6b7280', marginTop: 20, paddingTop: 10, borderTop: '1px solid #e5e7eb' },
}

export default function AutorizacaoGTA() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes, setLeiloes] = useState([])
  const [compradores, setCompradores] = useState([])
  const [leilaoId, setLeilaoId] = useState('')
  const [tipo, setTipo] = useState('compras')
  const [compradorId, setCompradorId] = useState('')
  const [dados, setDados] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingL, setLoadingL] = useState(true)
  const [loadingC, setLoadingC] = useState(false)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdf, setShowPdf] = useState(false)
  const iframeRef = useRef(null)

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .finally(() => setLoadingL(false))
  }, [orgId])

  useEffect(() => {
    if (!orgId || !leilaoId) { setCompradores([]); return }
    setLoadingC(true)
    setCompradorId('')
    setDados(null)
    mapasService.getCompradores(orgId, leilaoId, { tipo })
      .then(list => { setCompradores(list); setCompradorId(list.length ? 'todos' : '') })
      .catch(() => setCompradores([]))
      .finally(() => setLoadingC(false))
  }, [orgId, leilaoId, tipo])

  useEffect(() => {
    if (!orgId || !leilaoId || !compradorId) { setDados(null); return }
    if (compradorId === 'todos') { setDados(null); return }
    setLoading(true)
    mapasService.getAutorizacaoGTADados(orgId, leilaoId, compradorId, tipo)
      .then(setDados)
      .catch(() => setDados(null))
      .finally(() => setLoading(false))
  }, [orgId, leilaoId, compradorId, tipo])

  const leilaoAtual = leiloes.find(l => String(l.id) === leilaoId)
  const logoOrgSrc = organizadorAtivo?.logo_url
    ? (organizadorAtivo.logo_url.startsWith('http') ? organizadorAtivo.logo_url
        : `${API_BASE}${organizadorAtivo.logo_url.startsWith('/') ? '' : '/'}${organizadorAtivo.logo_url}`)
    : null

  const handleImprimir = async () => {
    setLoadingPdf(true)
    try {
      const blob = await mapasService.getAutorizacaoGTAPdf(orgId, leilaoId, compradorId, tipo)
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      setPdfUrl(url)
      setShowPdf(true)
    } catch (err) { alert('Erro: ' + (err.response?.statusText || err.message)) }
    finally { setLoadingPdf(false) }
  }
  const closePdf = () => { setShowPdf(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  if (!orgId) return <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">Selecione um organizador.</div>

  const totalQtd = dados?.lotes?.reduce((s, l) => s + l.qtd, 0) || 0
  const totalBruto = dados?.lotes?.reduce((s, l) => s + l.valor_total, 0) || 0
  const totalFiscal = dados?.lotes?.reduce((s, l) => s + l.tot_fiscal, 0) || 0
  const machos = dados?.lotes?.filter(l => (l.sexo || '').toUpperCase() === 'MACHO').reduce((s, l) => s + l.qtd, 0) || 0
  const femeas = dados?.lotes?.filter(l => ['FEMEA', 'FÊMEA'].includes((l.sexo || '').toUpperCase())).reduce((s, l) => s + l.qtd, 0) || 0
  const mistos = totalQtd - machos - femeas
  const criasM = dados?.lotes?.reduce((s, l) => s + (l.qtd_crias_machos || 0), 0) || 0
  const criasF = dados?.lotes?.reduce((s, l) => s + (l.qtd_crias_femeas || 0), 0) || 0
  const totalCrias = criasM + criasF

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <FileText className="w-5 h-5 text-teal-600" />
        <h1 className="text-lg font-semibold text-gray-800">Autorização GTA</h1>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Select label="Leilão" value={leilaoId} onChange={v => { setLeilaoId(v); setDados(null) }} disabled={loadingL} className="flex-[2] min-w-[180px]">
            {loadingL ? <option>Carregando...</option> : leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)}
          </Select>
          <Select label="Tipo" value={tipo} onChange={v => setTipo(v)} disabled={!leilaoId} className="flex-[1.5] min-w-[150px]">
            <option value="compras">COMPRAS</option>
            <option value="compras_defesas">COMPRAS E DEFESAS</option>
            <option value="defesas">DEFESAS</option>
          </Select>
          <Select label="Seleção" value={compradorId} onChange={v => { setCompradorId(v); setDados(null) }} disabled={loadingC || !leilaoId} className="flex-[2] min-w-[180px]">
            <option value="todos">TODOS</option>
            {compradores.map(c => <option key={c.id} value={String(c.id)}>{c.nome}</option>)}
          </Select>
          <div className="flex items-end gap-2 ml-auto">
            {loading && <RefreshCw size={15} className="text-gray-400 animate-spin mb-2" />}
            <button onClick={handleImprimir} disabled={(!dados && compradorId !== 'todos') || loadingPdf || !compradorId}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={15} /> {loadingPdf ? 'Gerando...' : 'Imprimir'}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
      ) : compradorId === 'todos' ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500 text-sm">
          <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="font-medium text-gray-600">Todos os compradores selecionados</p>
          <p className="mt-1">Clique em <strong>Imprimir</strong> para gerar o PDF com todas as autorizações GTA.</p>
        </div>
      ) : !dados ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">Selecione um comprador</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div style={S.page}>
            {/* Header */}
            <div style={S.hdr}>
              <div style={{ minWidth: 140 }}>{logoOrgSrc ? <img src={logoOrgSrc} alt="logo" style={S.hdrLogo} /> : <span style={{ fontWeight: 700 }}>{organizadorAtivo?.name || ''}</span>}</div>
              <div style={S.hdrCenter}>
                <div style={S.hdrT1}>AUTORIZAÇÃO PARA GTA</div>
                <div style={S.hdrT2}>{organizadorAtivo?.name || organizadorAtivo?.nome || ''}</div>
                <div style={S.hdrT3}>{leilaoAtual?.nome} — {fmtDate(leilaoAtual?.data_leilao)}</div>
              </div>
              <div style={{ minWidth: 140, textAlign: 'right' }}><img src="/assets/rematei.png" alt="Rematei" style={S.hdrLogo} /></div>
            </div>

            {/* Nome comprador banner */}
            <div style={S.compBanner}>{dados.comprador.nome}</div>

            {/* Dados comprador grid */}
            <div style={S.compGrid}>
              <div><span style={S.compLbl}>CPF/CNPJ:</span> {dados.comprador.cpf_cnpj || '—'}</div>
              <div><span style={S.compLbl}>CEP:</span> {dados.comprador.cep || '—'}</div>
              <div><span style={S.compLbl}>Fazenda:</span> {dados.comprador.fazenda || '—'}</div>
              <div><span style={S.compLbl}>Endereço:</span> {dados.comprador.endereco || '—'}</div>
              <div><span style={S.compLbl}>Telefone:</span> {dados.comprador.telefone || '—'}</div>
              <div><span style={S.compLbl}>I.E.:</span> {dados.comprador.fazenda_ie || '—'}</div>
              <div><span style={S.compLbl}>Município:</span> {dados.comprador.cidade}{dados.comprador.uf ? ` - ${dados.comprador.uf}` : ''}</div>
              <div><span style={S.compLbl}>Email:</span> {dados.comprador.email || '—'}</div>
              <div></div>
            </div>

            {/* Tabela de lotes */}
            <table style={S.tbl}>
              <thead><tr>
                <th style={S.th}>Lote</th>
                <th style={S.thC}>Qtd</th>
                <th style={S.thC}>Sexo</th>
                <th style={S.th}>Idade</th>
                <th style={S.th}>Vendedor</th>
                <th style={S.thR}>Unit. Fiscal</th>
                <th style={S.thR}>Tot. Fiscal</th>
                <th style={S.thR}>Valor de Venda</th>
                <th style={S.thR}>Total do Lote</th>
              </tr></thead>
              <tbody>
                {dados.lotes.map((l, i) => {
                  const criasParts = []
                  if (l.qtd_crias_machos) criasParts.push(`${l.qtd_crias_machos} macho(s)`)
                  if (l.qtd_crias_femeas) criasParts.push(`${l.qtd_crias_femeas} fêmea(s)`)
                  const subParts = []
                  if (criasParts.length) subParts.push(`Crias: ${criasParts.join(', ')}`)
                  if (l.observacoes?.trim()) subParts.push(`Obs: ${l.observacoes.trim()}`)
                  return (
                    <>
                      <tr key={i}>
                        <td style={S.td}><strong>{l.numero_lote}</strong></td>
                        <td style={S.tdC}>{l.qtd}</td>
                        <td style={S.tdC}>{l.sexo === 'Macho' ? 'M' : l.sexo === 'Fêmea' || l.sexo === 'Femea' ? 'F' : l.sexo || '—'}</td>
                        <td style={S.td}>{l.idade || '—'}</td>
                        <td style={S.td}>{l.vendedor}</td>
                        <td style={S.tdR}>{brl(l.unit_fiscal)}</td>
                        <td style={S.tdR}>{brl(l.tot_fiscal)}</td>
                        <td style={S.tdR}>{brl(l.valor_venda)}</td>
                        <td style={S.tdR}>{brl(l.valor_total)}</td>
                      </tr>
                      {subParts.length > 0 && (
                        <tr key={`${i}-sub`}>
                          <td colSpan={9} style={{ ...S.td, background: '#fafafa', fontSize: 11, fontWeight: 700, color: '#374151', padding: '3px 8px' }}>
                            {subParts.join(' | ')}
                          </td>
                        </tr>
                      )}
                    </>
                  )
                })}
              </tbody>
            </table>

            {/* Resumo por sexo + totais */}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '2px solid #1f2937', borderBottom: '1px solid #d1d5db', fontSize: 12, fontWeight: 700, marginTop: 8 }}>
              <span>QTD. MACHOS: {machos}</span>
              <span>QTD. FÊMEAS: {femeas}</span>
              <span>QTD. MISTO: {mistos}</span>
              {totalCrias > 0 && <span>CRIAS: {criasM} M / {criasF} F</span>}
              <span>TOTAL QTD: {totalQtd + totalCrias}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 12, fontWeight: 700 }}>
              <span>TOTAL GERAL PARA FISCAL (BRUTO - COMISSÃO): {brl(totalFiscal)}</span>
              <span>TOTAL BRUTO: {brl(totalBruto)}</span>
            </div>

            {/* Resumo agrupado por sexo+idade */}
            {dados.agrupado && dados.agrupado.length > 0 && (
              <>
                <div style={S.secTitle}>Resumo por Sexo e Idade</div>
                <table style={{ ...S.tbl, fontSize: 11 }}>
                  <thead><tr>
                    <th style={{ ...S.th, fontSize: 10 }}>Sexo</th>
                    <th style={{ ...S.th, fontSize: 10 }}>Idade</th>
                    <th style={{ ...S.thC, fontSize: 10 }}>Qtd</th>
                    <th style={{ ...S.thR, fontSize: 10 }}>Valor Total</th>
                  </tr></thead>
                  <tbody>
                    {dados.agrupado.map((g, i) => (
                      <tr key={i}>
                        <td style={S.td}>{g.sexo}</td>
                        <td style={S.td}>{g.idade}</td>
                        <td style={S.tdC}><strong>{g.qtd}</strong></td>
                        <td style={S.tdR}>{brl(g.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* Resumo das crias */}
            {totalCrias > 0 && (
              <>
                <div style={S.secTitle}>Resumo das Crias</div>
                <table style={{ ...S.tbl, fontSize: 11, width: '50%' }}>
                  <thead><tr>
                    <th style={{ ...S.th, fontSize: 10 }}>Sexo</th>
                    <th style={{ ...S.thC, fontSize: 10 }}>Qtd</th>
                  </tr></thead>
                  <tbody>
                    {criasM > 0 && <tr><td style={S.td}>Macho</td><td style={S.tdC}><strong>{criasM}</strong></td></tr>}
                    {criasF > 0 && <tr><td style={S.td}>Fêmea</td><td style={S.tdC}><strong>{criasF}</strong></td></tr>}
                    <tr><td style={S.td}><strong>Total</strong></td><td style={S.tdC}><strong>{totalCrias}</strong></td></tr>
                  </tbody>
                </table>
              </>
            )}

            {/* Autorização */}
            <div style={{ marginTop: 20, fontSize: 12, lineHeight: 1.8 }}>
              <div><strong>{organizadorAtivo?.name || organizadorAtivo?.nome || ''}</strong>, autoriza a emissão do GTA de <strong>{totalQtd} animais</strong>{totalCrias > 0 ? <> e <strong>{totalCrias} crias</strong> ({criasM} macho(s) e {criasF} fêmea(s))</> : ' e suas crias, se for o caso,'} dos lotes acima discriminados.</div>
              <div style={{ marginTop: 12, color: '#6b7280', fontSize: 11 }}>
                {organizadorAtivo?.cidade || ''}{organizadorAtivo?.uf ? ` - ${organizadorAtivo.uf}` : ''}, {new Date().toLocaleDateString('pt-BR')}
              </div>
            </div>

            {/* Assinatura */}
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 30 }}>
              <div style={{ textAlign: 'left', minWidth: 250 }}>
                <div style={{ borderBottom: '1px solid #1f2937', marginBottom: 6, width: 200 }}></div>
                <div style={{ fontSize: 12, fontWeight: 700 }}>{organizadorAtivo?.name || organizadorAtivo?.nome || ''}</div>
              </div>
            </div>

            <div style={S.footer}>www.rematei.com.br</div>
          </div>
        </div>
      )}

      {showPdf && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ height: '90vh' }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800">Autorização GTA — {compradorId === 'todos' ? 'Todos os Compradores' : dados?.comprador?.nome}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadPdf(pdfUrl, `Autorização GTA - ${compradorId === 'todos' ? 'Todos' : (dados?.comprador?.nome || '')}.pdf`)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors">Salvar PDF</button>
                <button onClick={printPdf} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"><Printer size={15} /> Imprimir</button>
                <button onClick={closePdf} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">Fechar</button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl"><iframe ref={iframeRef} src={pdfUrl} title="Autorização GTA" style={{ width: '100%', height: '100%', border: 'none' }} /></div>
          </div>
        </div>
      )}
    </div>
  )
}
